import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, type RecurrenceRow } from '../../lib/api';
import { SettingsCard } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { IconButton, RefreshIcon, TrashIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Recurring orders.
 *
 * A maintenance contract is the same visit every month, and entering it twelve
 * times is how one of them gets missed. A recurrence is the order plus the rule
 * for when it repeats; generating from it produces ordinary orders, which is
 * why nothing else on this screen or on the board has to know recurrence
 * exists.
 *
 * Generating is deliberately a button rather than something that happens on a
 * timer. A dispatcher wants to know a fortnight has just appeared on the board,
 * and wants to choose the moment.
 */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TYPES = [
  { value: 'DAILY', label: 'Every day' },
  { value: 'WEEKLY', label: 'Every week' },
  { value: 'MONTHLY', label: 'Every month' },
  { value: 'YEARLY', label: 'Every year' },
];

/** The rule in a sentence, because nobody reads it as four separate fields. */
function describe(r: RecurrenceRow): string {
  const p = r.recurrencePeriod;
  const every = p.period > 1 ? `every ${p.period} ` : 'every ';
  let when: string;
  if (p.recurrenceType === 'DAILY') when = `${every}day`;
  else if (p.recurrenceType === 'WEEKLY') {
    when = `${every}week on ${WEEKDAYS[p.weekday] ?? 'a set day'}`;
  } else if (p.recurrenceType === 'MONTHLY') {
    when = p.cardinal > 0
      ? `${every}month on the ${ordinal(p.cardinal)} ${WEEKDAYS[p.weekday] ?? 'day'}`
      : `${every}month on the same date`;
  } else when = `${every}year`;

  const ends =
    r.recurrenceEndType === 'ON_DATE' && r.endDate
      ? `, until ${humanDate(r.endDate)}`
      : r.recurrenceEndType === 'AFTER_N' && r.numOfRecurrences > 0
        ? `, ${r.numOfRecurrences} times`
        : '';
  return `Repeats ${when}${ends}`;
}

const ordinal = (n: number) => `${n}${['th', 'st', 'nd', 'rd'][n % 10 > 3 || (n > 10 && n < 14) ? 0 : n % 10] ?? 'th'}`;

/** Their dates are compact: 20260901. */
const humanDate = (compact: string | null) => {
  if (!compact) return '';
  const iso = /^\d{8}$/.test(compact)
    ? `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6)}`
    : compact;
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString();
};

const toCompact = (iso: string) => iso.replace(/-/g, '');

export function RecurrencesPage({ territoryId }: { territoryId: string | null }) {
  const ui = useUi();
  const [rows, setRows] = useState<RecurrenceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [serviceMinutes, setServiceMinutes] = useState('30');
  const [type, setType] = useState('WEEKLY');
  const [weekday, setWeekday] = useState('1');
  const [period, setPeriod] = useState('1');
  const [startDate, setStartDate] = useState(today);

  const load = useCallback(async () => {
    if (!territoryId) return;
    setLoading(true);
    try {
      const { recurrences } = await api.recurrences(territoryId);
      setRows(Object.values(recurrences));
    } catch (err) {
      ui.notify(
        'error',
        'Could not load recurrences',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setLoading(false);
    }
  }, [territoryId, ui]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!territoryId) return;
    if (!name.trim() || !address.trim()) {
      ui.notify('info', 'Needs a name and an address', 'Both appear on every order it produces.');
      return;
    }
    setBusy(true);
    try {
      await api.addRecurrence(territoryId, {
        recurrencePeriod: {
          recurrenceType: type,
          period: Number(period) || 1,
          weekday: type === 'WEEKLY' ? Number(weekday) : -1,
          cardinal: 0,
          month: -1,
        },
        recurrenceEndType: 'NEVER',
        numOfRecurrences: -1,
        startDate: toCompact(startDate),
        orderTemplate: {
          /*
           * The template's own id becomes the stem of every generated order's
           * reference, so a generated day can be recognised and regenerating
           * does not duplicate.
           */
          id: `REC-${name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').slice(0, 24)}`,
          name: name.trim(),
          priority: 50,
          delivery: {
            location: { address: address.trim() },
            serviceTimeSec: Math.max(60, Number(serviceMinutes) * 60 || 1800),
          },
        },
      });
      ui.notify('ok', 'Recurrence added', 'Generate to put its orders on the board.');
      setName('');
      setAddress('');
      setAdding(false);
      await load();
    } catch (err) {
      ui.notify('error', 'Could not add', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!territoryId) return;
    setBusy(true);
    try {
      const { generated } = await api.generateRecurrentOrders(territoryId);
      ui.notify(
        'ok',
        generated > 0 ? `${generated} order${generated === 1 ? '' : 's'} created` : 'Nothing new',
        generated > 0
          ? 'They are on the board for the days the rules name.'
          : 'Every order these rules imply inside the planning horizon already exists.',
      );
    } catch (err) {
      ui.notify('error', 'Could not generate', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const end = async (r: RecurrenceRow) => {
    if (!territoryId) return;
    const ok = await ui.confirm({
      title: 'Stop this recurrence?',
      body: 'No further orders will be generated from it. Orders it has already produced stay on their days.',
    });
    if (!ok) return;
    try {
      await api.endRecurrence(territoryId, r.id, null);
      ui.notify('ok', 'Stopped');
      await load();
    } catch (err) {
      ui.notify('error', 'Could not stop', err instanceof ApiError ? err.message : String(err));
    }
  };

  if (!territoryId) return <div className="empty">Select a territory.</div>;

  return (
    <>
      <SettingsCard
        title="Recurring orders"
        description="A template plus a rule for when it repeats. Generating turns the rule into ordinary orders on the days it names — nothing downstream treats them differently."
        /* A read-only card: its own controls live in the header, and without
           this the card treats itself as mid-edit and hides them. */
        editing={false}
        actions={
          <>
            <IconButton label="Refresh" onClick={() => void load()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <button className="btn" type="button" onClick={() => void generate()} disabled={busy}>
              {busy ? 'Working' : 'Generate orders'}
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => setAdding((v) => !v)}
            >
              {adding ? 'Cancel' : 'New recurrence'}
            </button>
          </>
        }
      >
        {adding ? (
          <div className="recur-form">
            <label className="field-row" data-wide="true">
              <span className="field-label">Name</span>
              <span className="field-control">
                <input
                  className="field"
                  value={name}
                  placeholder="Monthly filter change — Acme Laundromat"
                  onChange={(e) => setName(e.target.value)}
                />
              </span>
            </label>
            <label className="field-row" data-wide="true">
              <span className="field-label">Address</span>
              <span className="field-control">
                <input
                  className="field"
                  value={address}
                  placeholder="400 Vine St, Cincinnati, OH 45202"
                  onChange={(e) => setAddress(e.target.value)}
                />
              </span>
            </label>
            <label className="field-row">
              <span className="field-label">
                Service time
                <small>minutes on site</small>
              </span>
              <span className="field-control">
                <input
                  className="field"
                  type="number"
                  min="5"
                  value={serviceMinutes}
                  onChange={(e) => setServiceMinutes(e.target.value)}
                />
              </span>
            </label>
            <label className="field-row">
              <span className="field-label">Repeats</span>
              <span className="field-control">
                <Select value={type} options={TYPES} onChange={setType} />
              </span>
            </label>
            {type === 'WEEKLY' ? (
              <label className="field-row">
                <span className="field-label">On</span>
                <span className="field-control">
                  <Select
                    value={weekday}
                    options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
                    onChange={setWeekday}
                  />
                </span>
              </label>
            ) : null}
            <label className="field-row">
              <span className="field-label">
                Every
                <small>1 is every time</small>
              </span>
              <span className="field-control">
                <input
                  className="field"
                  type="number"
                  min="1"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                />
              </span>
            </label>
            <label className="field-row">
              <span className="field-label">Starting</span>
              <span className="field-control">
                <DatePicker value={startDate} today={today} onChange={setStartDate} />
              </span>
            </label>
            <div className="recur-actions">
              <button className="btn btn-primary" type="button" onClick={() => void create()} disabled={busy}>
                {busy ? 'Adding' : 'Add recurrence'}
              </button>
            </div>
          </div>
        ) : null}

        {rows.length === 0 && !adding ? (
          <p className="hint">
            Nothing repeats yet. A recurrence saves entering the same visit every month, which
            is where one of them gets missed.
          </p>
        ) : null}

        {rows.length > 0 ? (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Rule</th>
                  <th>Starts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{String(r.orderTemplate?.name ?? 'Untitled')}</strong>
                      <span className="dim">
                        {' '}
                        {String(
                          (r.orderTemplate?.delivery as { location?: { address?: string } })
                            ?.location?.address ?? '',
                        )}
                      </span>
                    </td>
                    <td>{describe(r)}</td>
                    <td>{humanDate(r.startDate)}</td>
                    <td>
                      <IconButton label="Stop this recurrence" tone="danger" onClick={() => void end(r)}>
                        <TrashIcon />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SettingsCard>
    </>
  );
}
